import { Button } from '@nerdwallet/currency';
import MarketplaceStarRating from '@nerdwallet/react-marketplace-star-rating';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import Image from 'next/image';
import { forwardRef } from 'react';
import { VERTICALS } from '~/app/api/insurance-matches-services/media-alpha-service/constants';
import SegmentImpression from '../../segment-impression';
import styles from '../auto-insurance-matches.module.scss';
import { PLATFORM } from '../constants';
import { useSkuViewedUrl } from '../utils';

type MatchesOfferCardProps = {
  offer: any & { url: string };
  offerIndex: number;
  offerTotal: number;
};

const OfferCardContent = forwardRef<
  HTMLDivElement,
  MatchesOfferCardProps & { skuViewedPayload?: any }
>(({ offer, offerIndex, offerTotal, skuViewedPayload }, ref) => {
  const url = useSkuViewedUrl(offer.url, skuViewedPayload, {
    offerTotal,
    offerPosition: offerIndex + 1,
  });

  return (
    <div ref={ref} className={styles.offerCardWrapper}>
      <div className={styles.offerCard}>
        <div className={styles.offerCardHeader}>
          <div className={styles.offerCardContent}>
            <div className={styles.offerImageContainer}>
              {offer.imageSource && (
                <Image
                  src={offer.imageSource}
                  alt={offer.displayName}
                  fill
                  style={{ objectFit: 'contain', objectPosition: 'left' }}
                />
              )}
            </div>
            <a
              href={url('Product_name')}
              className={styles.offerLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h6 className={styles.offerTitle}>{offer.displayName}</h6>
            </a>
          </div>
          <div className={styles.offerRating}>
            {offer.starRating ? (
              <MarketplaceStarRating
                starRating={offer.starRating}
                layout="small-numeric"
              />
            ) : (
              <span className={styles.notRatedText}>Not yet rated</span>
            )}
          </div>
        </div>
        <div className={styles.offerCardFooter}>
          {offer.cta && (
            <a
              href={url('CTA')}
              className={styles.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className={styles.ctaButton} primary>
                {offer.cta.text}
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
});

const MatchesOfferCard = forwardRef<HTMLDivElement, MatchesOfferCardProps>(
  ({ offer, offerIndex, offerTotal }, ref) => {
    const { pageViewId, pageName } = useAnalyticsPageView();
    const skuViewedPayload = {
      sku_id: offer.id,
      sku_source: 'marketplace',
      sku_name: offer.displayName,
      sku_product_type: VERTICALS.AUTO,
      sku_level: 'offer',
      offer_id: offer.id,
      offer_name: offer.displayName,
      offer_source: PLATFORM.MEDIA_ALPHA,
      monetizable: 'yes_assumed',
      impression_page_name: pageName || window.location.pathname,
      impression_page_url: window.location.href,
      impression_pageview_id: pageViewId,
      impression_section: 'auto_insurance_click_table',
      impression_position: offerIndex,
      entity_name: `offer_id_${offer.id}`,
      location: 'auto-insurance-matches',
      sublocation: 'auto-insurance-matches',
    };
    return (
      <SegmentImpression eventName="SKU Viewed" eventProps={skuViewedPayload}>
        <OfferCardContent
          skuViewedPayload={skuViewedPayload}
          offer={offer}
          offerIndex={offerIndex}
          offerTotal={offerTotal}
          ref={ref}
        />
      </SegmentImpression>
    );
  }
);

export default MatchesOfferCard;
